import { useInfiniteQuery } from "@tanstack/react-query";
import { USERAXIOS } from "../../api";
import { useMemo } from "react";
import { ResumeSubscription } from "../../types/orders-managment/subscriptions";

interface ResumeSubscriptionsResponse {
  data: ResumeSubscription[];
  meta: {
    current_page: number;
    last_page: number;
    per_page: number;
    total: number;
  };
}

export const useResumeSubscription = () => {
  return useInfiniteQuery({
    queryKey: ["resumeSubscriptions"],
    queryFn: async ({
      pageParam = 1,
    }): Promise<ResumeSubscriptionsResponse> => {
      const response = await USERAXIOS.get<ResumeSubscriptionsResponse>(
        "/resume_subscriptions",
        {
          params: {
            page: pageParam,
            per_page: 100,
          },
        }
      );
      return response.data;
    },
    getNextPageParam: (lastPage) => {
      return lastPage.meta.current_page < lastPage.meta.last_page
        ? lastPage.meta.current_page + 1
        : undefined;
    },
    initialPageParam: 1,
  });
};

export const useResumeSubscriptionInfinite = (
  pageSize: number = 20,
  searchTerm: string = ""
) => {
  return useInfiniteQuery({
    queryKey: ["resumeSubscriptions", "infinite", pageSize, searchTerm],
    queryFn: async ({
      pageParam = 1,
    }): Promise<ResumeSubscriptionsResponse> => {
      const response = await USERAXIOS.get<ResumeSubscriptionsResponse>(
        `/resume_subscriptions?search=${searchTerm}&page=${pageParam}&per_page=${pageSize}`
      );
      return response.data;
    },
    getNextPageParam: (lastPage) => {
      return lastPage.meta.current_page < lastPage.meta.last_page
        ? lastPage.meta.current_page + 1
        : undefined;
    },
    initialPageParam: 1,
  });
};

// Hook for autocomplete usage
export const useResumeSubscriptionForAutocomplete = (
  pageSize: number = 20,
  searchTerm: string = ""
) => {
  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    error,
    refetch,
  } = useInfiniteQuery({
    queryKey: ["resumeSubscriptions", "infinite", pageSize, searchTerm],
    queryFn: async ({
      pageParam = 1,
    }): Promise<ResumeSubscriptionsResponse> => {
      const response = await USERAXIOS.get<ResumeSubscriptionsResponse>(
        `/resume_subscriptions`,
        {
          params: {
            page: pageParam,
            per_page: pageSize,
            search: searchTerm || undefined,
          },
        }
      );
      return response.data;
    },
    getNextPageParam: (lastPage) => {
      return lastPage.meta.current_page < lastPage.meta.last_page
        ? lastPage.meta.current_page + 1
        : undefined;
    },
    initialPageParam: 1,
  });

  // Flatten all pages into a single array
  const resumeSubscriptions = useMemo(() => {
    return data?.pages.flatMap((page) => page.data) || [];
  }, [data]);

  const loadMore = () => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  };

  return {
    items: resumeSubscriptions,
    loading: isLoading || isFetchingNextPage,
    hasMore: hasNextPage,
    loadMore,
    error,
    refetch,
    totalPages: data?.pages[0]?.meta.last_page || 0,
    totalItems: data?.pages[0]?.meta.total || 0,
  };
};
